import addUrlQueryParams from "common/utils/url";

type Options = {
  auto?: string;
  format?: string;
  w?: number;
  h?: number;
  dpr?: number;
  fit?: string;
  q?: number;
  [key: string]: string | number | undefined;
};

export type ImgixBaseOptions = {
  fit?: string;
  auto?: string;
  quality?: number;
};

export type ImgixThumbnailOptions = ImgixBaseOptions & {
  width: number;
};

export type ImgixModalOptions = ImgixBaseOptions & {
  maxWidth: number;
  capWidth: number;
  zoomHeadroom: number;
  dprCap: number;
  ssrFallbackWidth: number;
};

function buildQueryString(options: Options): string {
  const params = new URLSearchParams();

  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

function buildImgixParams(width: number, options: ImgixBaseOptions): Record<string, string> {
  return {
    w: String(width),
    fit: options.fit ?? "max",
    auto: options.auto ?? "format,compress",
    q: String(options.quality ?? 75)
  };
}

/**
 * Generate remote image URL using the host from backend or default one
 * @param path - The path to the image (e.g., "gen/ai-loader.gif")
 * @param options - Optional parameters
 * @returns The complete imgix URL
 */
export function remoteImageStaticUrl(path: string, options: Options = {}): string {
  if (!path) return "";
  const host = window.jiffy?.remoteImageHost; // provided in frontend_routes
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  const queryString = buildQueryString(options);
  return `${host}/${cleanPath}${queryString}`;
}

function isInlineOrBlobUrl(url: string): boolean {
  return url.startsWith("data:") || url.startsWith("blob:");
}

/**
 * Returns an imgix URL with thumbnail params applied.
 * Data and blob URLs are returned unchanged (e.g. frontend-generated composite previews).
 */
export function artworkImgixThumbnailUrl(
  url: string | null | undefined,
  options: ImgixThumbnailOptions
): string | null | undefined {
  if (!url || typeof url !== "string") return url;
  if (isInlineOrBlobUrl(url)) return url;
  return addUrlQueryParams(url, buildImgixParams(options.width, options));
}

/**
 * Max width in pixels for an image viewer: viewport × DPR × headroom, capped.
 */
export function artworkModalTargetWidth(options: ImgixModalOptions): number {
  if (typeof window === "undefined") return options.ssrFallbackWidth;
  const vw = Math.min(window.innerWidth, options.capWidth);
  const dpr = Math.min(window.devicePixelRatio || 1, options.dprCap);
  return Math.min(Math.ceil(vw * dpr * options.zoomHeadroom), options.maxWidth);
}

/**
 * Returns an imgix URL capped for an artwork preview modal.
 * Call when the modal opens so the requested size matches viewport and DPR.
 * Data and blob URLs are returned unchanged (e.g. frontend-generated composite previews).
 */
export function artworkImgixModalUrl(
  url: string | null | undefined,
  options: ImgixModalOptions
): string | null | undefined {
  if (!url || typeof url !== "string") return url;
  if (isInlineOrBlobUrl(url)) return url;
  const width = Math.min(artworkModalTargetWidth(options), options.maxWidth);
  return addUrlQueryParams(url, buildImgixParams(width, options));
}
